import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { showingbookerAPI } from '../../../services/showingbookerAPI';

const StatsContainer = styled.div`
  padding: 20px 0;
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 30px;
  
  h2 {
    margin: 0 0 8px 0;
    color: #333;
    font-size: 28px;
    font-weight: 700;
  }
  
  .subtitle {
    font-size: 16px;
    color: #666;
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
`;

const MetricCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border-left: 4px solid ${props => props.color || '#007AFF'};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  }
`;

const MetricHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const MetricIcon = styled.span`
  font-size: 24px;
`;

const MetricTrend = styled.span`
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: 500;
  background: ${props => props.positive ? '#d4edda' : props.negative ? '#f8d7da' : '#fff3cd'};
  color: ${props => props.positive ? '#155724' : props.negative ? '#721c24' : '#856404'};
`;

const MetricValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
`;

const MetricLabel = styled.div`
  font-size: 14px;
  color: #666;
  font-weight: 500;
`;

const SectionTitle = styled.h3`
  margin: 0 0 20px 0;
  color: #333;
  font-size: 20px;
  font-weight: 600;
`;

const TopPerformersSection = styled.div`
  margin-bottom: 40px;
`;

const PerformersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
`;

const PerformerCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  
  .rank {
    display: inline-block;
    width: 30px;
    height: 30px;
    background: ${props => 
      props.rank === 1 ? '#FFD700' : 
      props.rank === 2 ? '#C0C0C0' : 
      props.rank === 3 ? '#CD7F32' : '#f0f0f0'};
    color: ${props => props.rank <= 3 ? 'white' : '#666'};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 14px;
    margin-bottom: 12px;
  }
  
  .address {
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
    font-size: 16px;
  }
  
  .metrics {
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    color: #666;
  }
  
  .metric {
    text-align: center;
    
    .value {
      font-weight: 600;
      color: #007AFF;
      display: block;
    }
    
    .label {
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  
  .icon {
    font-size: 48px;
    margin-bottom: 16px;
  }
  
  h3 {
    margin: 0 0 8px 0;
    color: #333;
  }
  
  p {
    margin: 0;
    color: #666;
    font-size: 14px;
  }
`;

const Stats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      
      // Load dashboard stats and properties
      const [dashboardStats, propertiesData] = await Promise.all([
        showingbookerAPI.getDashboardStats(),
        showingbookerAPI.getProperties()
      ]);
      
      // Calculate additional metrics
      const enhancedStats = {
        ...dashboardStats,
        averageBookingsPerProperty: propertiesData.length > 0 ? 
          (dashboardStats.totalBookings / propertiesData.length).toFixed(1) : 0,
        totalProperties: propertiesData.length,
        activeProperties: propertiesData.filter(p => p.status === 'listed').length,
        conversionRate: dashboardStats.totalViews > 0 ? 
          ((dashboardStats.totalBookings / dashboardStats.totalViews) * 100).toFixed(1) : 0
      };
      
      setStats(enhancedStats);
      setProperties(propertiesData);
      
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };


  // Calculate top performing properties (real data only)
  const topProperties = properties
    .map(property => ({
      ...property,
      bookingCount: property.showings_booked || 0
    }))
    .sort((a, b) => b.bookingCount - a.bookingCount)
    .slice(0, 6);

  if (loading) {
    return (
      <StatsContainer>
        <LoadingState>Loading your analytics...</LoadingState>
      </StatsContainer>
    );
  }

  if (!stats || stats.totalProperties === 0) {
    return (
      <StatsContainer>
        <PageHeader>
          <h2>📈 Stats & Analytics</h2>
          <div className="subtitle">Performance insights for your properties</div>
        </PageHeader>
        
        <EmptyState>
          <div className="icon">📊</div>
          <h3>No Data Yet</h3>
          <p>Add some properties and start getting bookings to see your analytics here!</p>
        </EmptyState>
      </StatsContainer>
    );
  }

  return (
    <StatsContainer>
      <PageHeader>
        <h2>📈 Stats & Analytics</h2>
        <div className="subtitle">Performance insights for your {stats.totalProperties} properties</div>
      </PageHeader>

      {/* Key Metrics */}
      <MetricsGrid>
        <MetricCard color="#007AFF">
          <MetricHeader>
            <MetricIcon>🏠</MetricIcon>
            <MetricTrend positive>Active</MetricTrend>
          </MetricHeader>
          <MetricValue>{stats.totalProperties}</MetricValue>
          <MetricLabel>Total Properties</MetricLabel>
        </MetricCard>

        <MetricCard color="#28a745">
          <MetricHeader>
            <MetricIcon>📅</MetricIcon>
            <MetricTrend positive>Total</MetricTrend>
          </MetricHeader>
          <MetricValue>{stats.totalBookings || 0}</MetricValue>
          <MetricLabel>Total Bookings</MetricLabel>
        </MetricCard>

        <MetricCard color="#17a2b8">
          <MetricHeader>
            <MetricIcon>👀</MetricIcon>
            <MetricTrend positive>Views</MetricTrend>
          </MetricHeader>
          <MetricValue>{stats.totalViews || 0}</MetricValue>
          <MetricLabel>Total Views</MetricLabel>
        </MetricCard>

        <MetricCard color="#ffc107">
          <MetricHeader>
            <MetricIcon>📊</MetricIcon>
            <MetricTrend positive>Rate</MetricTrend>
          </MetricHeader>
          <MetricValue>{stats.conversionRate}%</MetricValue>
          <MetricLabel>Conversion Rate</MetricLabel>
        </MetricCard>

        <MetricCard color="#6f42c1">
          <MetricHeader>
            <MetricIcon>⏰</MetricIcon>
            <MetricTrend positive>Average</MetricTrend>
          </MetricHeader>
          <MetricValue>{stats.averageBookingsPerProperty}</MetricValue>
          <MetricLabel>Avg Bookings/Property</MetricLabel>
        </MetricCard>

        <MetricCard color="#20c997">
          <MetricHeader>
            <MetricIcon>💰</MetricIcon>
            <MetricTrend positive>Estimated</MetricTrend>
          </MetricHeader>
          <MetricValue>${(stats.totalBookings * 75).toLocaleString()}</MetricValue>
          <MetricLabel>Estimated Revenue</MetricLabel>
        </MetricCard>
      </MetricsGrid>


      {/* Top Performers */}
      {topProperties.length > 0 && (
        <TopPerformersSection>
          <SectionTitle>🏆 Top Performing Properties</SectionTitle>
          <PerformersGrid>
            {topProperties.map((property, index) => (
              <PerformerCard key={property.id} rank={index + 1}>
                <div className="rank">{index + 1}</div>
                <div className="address">
                  {property.address_line1}
                  {property.unit_number && `, ${property.unit_number}`}
                </div>
                <div className="metrics">
                  <div className="metric">
                    <span className="value">{property.bookingCount}</span>
                    <span className="label">Bookings</span>
                  </div>
                  <div className="metric">
                    <span className="value">{property.status}</span>
                    <span className="label">Status</span>
                  </div>
                  <div className="metric">
                    <span className="value">{property.city}</span>
                    <span className="label">City</span>
                  </div>
                </div>
              </PerformerCard>
            ))}
          </PerformersGrid>
        </TopPerformersSection>
      )}
    </StatsContainer>
  );
};

export default Stats;